import { ENVIRONMENT_CONFIG } from '@abb-procure/constants';
import { DestroyRef, inject, signal } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { type SilentRequest } from '@azure/msal-browser';
import {
  type HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  type IRetryPolicy,
  LogLevel,
} from '@microsoft/signalr';
import { firstValueFrom, map, take } from 'rxjs';

export type SignalrConnectionState =
  'disconnected' | 'connecting' | 'connected';

export interface SignalrConnectionConfig {
  url: string;
  headers?: Record<string, string>;
  reconnectPolicy?: IRetryPolicy;
  logLevel?: LogLevel;
}

export abstract class SignalrBaseService {
  protected readonly connectionState =
    signal<SignalrConnectionState>('disconnected');
  protected readonly reconnectCount = signal(0);
  protected readonly environmentConfig = inject(ENVIRONMENT_CONFIG);
  protected hub: HubConnection | null = null;
  protected readonly destroyRef = inject(DestroyRef);
  protected logPrefix = this.constructor.name;

  private readonly msalService = inject(MsalService);

  protected constructor() {
    this.destroyRef.onDestroy(() => {
      void this.stopConnection();
    });
  }

  protected async startConnection(
    config: SignalrConnectionConfig,
    configure?: (hub: HubConnection) => void,
  ): Promise<void> {
    if (this.hub?.state === HubConnectionState.Connected) {
      return;
    }

    if (
      this.hub?.state === HubConnectionState.Connecting ||
      this.hub?.state === HubConnectionState.Reconnecting
    ) {
      return;
    }

    this.connectionState.set('connecting');

    if (!this.hub) {
      const builder = new HubConnectionBuilder()
        .withUrl(config.url, {
          accessTokenFactory: () => this.getAuthToken(),
          headers: config.headers,
        })
        .configureLogging(
          config.logLevel ??
            (this.environmentConfig.signalrLoggingEnabled
              ? LogLevel.Information
              : LogLevel.Error),
        );

      if (config.reconnectPolicy) {
        builder.withAutomaticReconnect(config.reconnectPolicy);
      } else {
        builder.withAutomaticReconnect();
      }

      this.hub = builder.build();

      configure?.(this.hub);

      this.hub.onreconnecting(() => {
        this.connectionState.set('connecting');
      });

      this.hub.onreconnected(() => {
        this.connectionState.set('connected');
        this.reconnectCount.update((count) => count + 1);
      });

      this.hub.onclose(() => {
        this.connectionState.set('disconnected');
      });
    }

    try {
      await this.hub.start();
      this.connectionState.set('connected');
    } catch (error) {
      this.log('Connection failed', error);
      this.connectionState.set('disconnected');
      throw error;
    }
  }

  protected async stopConnection(): Promise<void> {
    if (!this.hub) {
      return;
    }

    try {
      await this.hub.stop();
      this.connectionState.set('disconnected');
    } catch (error) {
      this.log('Failed to stop connection', error);
    }
  }

  protected getAuthToken(): Promise<string> {
    return firstValueFrom(
      this.msalService
        .acquireTokenSilent({
          account: this.msalService.instance.getActiveAccount(),
          scopes: [
            `api://${this.environmentConfig.msalConfig.apiEndpointId}/read_as_user`,
          ],
        } as SilentRequest)
        .pipe(
          map((token) => token.accessToken),
          take(1),
        ),
    );
  }

  protected log(message: string, error?: unknown): void {
    if (!this.environmentConfig.signalrLoggingEnabled) {
      return;
    }

    if (error) {
      console.error(`${this.logPrefix}: ${message}`, error);
      return;
    }

    console.info(`${this.logPrefix}: ${message}`);
  }
}
